import React from 'react';
import PropTypes from 'prop-types';
import { Box, Container, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import RevealOnScroll from '../components/RevealOnScroll';
import AnimatedCounter from '../components/AnimatedCounter';

/**
 * Full-width navy strip with four live platform stats.
 * Accepts real data from useLibraries() via parent props.
 * liveLabel / stats props are used by the admin layout system to customise
 * copy; dynamic values (totalCount, uniqueCities) always come from the live hook.
 */
function SocialProofStrip({ totalCount, uniqueCities, loading, liveLabel, stats }) {
  // Resolve overridable static stats (seats + support) from props when provided.
  // Dynamic stats (libraries, cities) always use live hook values.
  const seatsEntry = stats ? stats.find((s) => s.dynamic === null && s.id !== 'support') : null;
  const seatsValue = seatsEntry ? seatsEntry.value : 400000;
  const seatsSuffix = seatsEntry ? (seatsEntry.suffix || '+') : '+';
  const seatsLabel = seatsEntry ? seatsEntry.label : 'Study seats available';
  const librariesEntry = stats ? stats.find((s) => s.dynamic === 'libraries') : null;
  const librariesLabel = librariesEntry ? librariesEntry.label : 'Study libraries listed';
  const librariesSuffix = librariesEntry ? (librariesEntry.suffix || '+') : '+';
  const citiesEntry = stats ? stats.find((s) => s.dynamic === 'cities') : null;
  const citiesLabel = citiesEntry ? citiesEntry.label : 'Cities across India';
  const citiesSuffix = citiesEntry ? (citiesEntry.suffix || '+') : '+';
  const supportEntry = stats ? stats.find((s) => s.id === 'support') : null;
  const supportLabel = supportEntry ? supportEntry.label : 'Support on WhatsApp';
  const theme = useTheme();
  const navy = theme.palette.brand.navy.dark;
  const orange = theme.palette.brand.orange.main;
  const golden = theme.palette.brand.golden.main;

  const valueStyle = {
    fontWeight: 800,
    background: `linear-gradient(135deg, ${orange}, ${golden})`,
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    fontSize: { xs: '1.75rem', md: '2.5rem' },
    lineHeight: 1.1,
    mb: 0.5,
    display: 'block',
  };

  const labelStyle = {
    color: 'rgba(255,255,255,0.7)',
    letterSpacing: '0.1em',
    fontSize: '0.7rem',
    fontWeight: 600,
  };

  const staticValueStyle = {
    ...valueStyle,
    // Static "24 × 7" renders as plain text, not animated counter
    WebkitTextFillColor: 'transparent',
  };

  return (
    <Box
      component="section"
      aria-label="Platform statistics"
      sx={{ background: navy, py: { xs: 5, md: 7 } }}
    >
      <Container maxWidth="lg">
        {/* LIVE badge */}
        <RevealOnScroll delay={0}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 1,
              mb: 4,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: orange,
                '@keyframes livePulse': {
                  '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                  '50%': { opacity: 0.4, transform: 'scale(1.5)' },
                },
                animation: 'livePulse 1.6s ease-in-out infinite',
              }}
              aria-hidden="true"
            />
            <Typography
              variant="overline"
              sx={{
                color: orange,
                fontWeight: 800,
                letterSpacing: '0.18em',
                fontSize: '0.7rem',
              }}
            >
              {liveLabel}
            </Typography>
          </Box>
        </RevealOnScroll>

        <Grid container spacing={{ xs: 2, md: 4 }} justifyContent="center">
          {/* Libraries listed */}
          <Grid item xs={6} md={3}>
            <RevealOnScroll delay={0}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" component="p" sx={valueStyle}>
                  {loading ? '—' : <AnimatedCounter to={totalCount} suffix={librariesSuffix} />}
                </Typography>
                <Typography variant="overline" sx={labelStyle}>
                  {librariesLabel}
                </Typography>
              </Box>
            </RevealOnScroll>
          </Grid>

          {/* Cities */}
          <Grid item xs={6} md={3}>
            <RevealOnScroll delay={0.08}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" component="p" sx={valueStyle}>
                  {loading ? '—' : <AnimatedCounter to={uniqueCities} suffix={citiesSuffix} />}
                </Typography>
                <Typography variant="overline" sx={labelStyle}>
                  {citiesLabel}
                </Typography>
              </Box>
            </RevealOnScroll>
          </Grid>

          {/* Seats */}
          <Grid item xs={6} md={3}>
            <RevealOnScroll delay={0.16}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" component="p" sx={valueStyle}>
                  <AnimatedCounter to={seatsValue} suffix={seatsSuffix} />
                </Typography>
                <Typography variant="overline" sx={labelStyle}>
                  {seatsLabel}
                </Typography>
              </Box>
            </RevealOnScroll>
          </Grid>

          {/* 24×7 support — static */}
          <Grid item xs={6} md={3}>
            <RevealOnScroll delay={0.24}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" component="p" sx={staticValueStyle}>
                  24×7
                </Typography>
                <Typography variant="overline" sx={labelStyle}>
                  {supportLabel}
                </Typography>
              </Box>
            </RevealOnScroll>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

SocialProofStrip.propTypes = {
  totalCount: PropTypes.number,
  uniqueCities: PropTypes.number,
  loading: PropTypes.bool,
  liveLabel: PropTypes.string,
  stats: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      value: PropTypes.number,
      label: PropTypes.string,
      dynamic: PropTypes.oneOf(['libraries', 'cities', null]),
      suffix: PropTypes.string,
      staticDisplay: PropTypes.string,
    })
  ),
};

SocialProofStrip.defaultProps = {
  totalCount: 0,
  uniqueCities: 0,
  loading: true,
  liveLabel: 'Live now',
  stats: null,
};

export default SocialProofStrip;
